import {
  DatabaseAuto, IcommerceUser, IdataArrayResponse, IecommerceOrder,
  IecommerceOrderRefund,
  IfilterProps,
  TecommerceOrderRefundRequest,
  validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../../commerce-client';

/**
 * Type alias for order refund properties with specific generic types
 * @template T - Type for order (default: IecommerceOrder<number, IcommerceUser>)
 * @template S - Type for order refund status (default: string)
 * @template U - Type for user (default: IcommerceUser)
 */
type TecommerceOrderRefundProps = IecommerceOrderRefund<IecommerceOrder<number, IcommerceUser>, string, IcommerceUser>;

/**
 * Represents an order refund for a specific order.
 * Extends DatabaseAuto to inherit common database operations.
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link EcommerceOrderRefund} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const orderrefund = await EcommerceOrderRefund.getAll();
 *
 * const orderrefund = await EcommerceOrderRefund.filterAll({
 *
 * });
 *
 * const orderrefund = await EcommerceOrderRefund.bulkDelete({
 *
 * });
 *
 * const orderrefund = await EcommerceOrderRefund.getOne('resourceId');
 *
 * const orderrefund = await EcommerceOrderRefund.remove();
 *
 * ```
 */
export class EcommerceOrderRefund
  extends DatabaseAuto {
  private static readonly baseUrl = '/order-refund';

  /** Order associated with the refund */
  readonly ecommerceOrder: TecommerceOrderRefundProps['ecommerceOrder'];

  /** Vendors associated with the refund */
  readonly ecommerceVendors: TecommerceOrderRefundProps['ecommerceVendors'];

  /** Date when the refund was initiated */
  readonly dateInitiated: TecommerceOrderRefundProps['dateInitiated'];

  /** Date when the refund was completed */
  readonly dateCompleted?: TecommerceOrderRefundProps['dateCompleted'];

  /** User associated with the refund */
  readonly commerceUser: TecommerceOrderRefundProps['commerceUser'];

  /** Status of the refund */
  readonly status: TecommerceOrderRefundProps['status'];

  /** Amount of the refund */
  readonly amount: TecommerceOrderRefundProps['amount'];

  /** Payment-related metadata */
  readonly paymentMeta?: TecommerceOrderRefundProps['paymentMeta'];

  /**
   * Constructs an instance of `EcommerceOrderRefund`.
   *
   * @param {TecommerceOrderRefundProps} data - The orderrefund data used to initialize the instance.
   * @throws {Error} Throws an error if no data is provided.
   */
  constructor(data: TecommerceOrderRefundProps) {
    validateData(data, {
      dateInitiated: {
        validate: (v) => {
          // Convert to Date if not already a Date object
          const dateInitiated = v instanceof Date ? v : new Date(v);

          // Check date validity
          return !isNaN(dateInitiated.getTime());
        },
        message: 'dateInitiated at must be a valid date (Date object or ISO date string)'
      },
      dateCompleted: {
        validate: (v) => {
          // Convert to Date if not already a Date object
          if (!v) {
            return true;
          }
          const dateCompleted = v instanceof Date ? v : new Date(v);

          // Check date validity
          return !isNaN(dateCompleted.getTime());
        },
        message: 'Created at must be a valid date (Date object or ISO date string)'
      }
    });

    super(data);

    this.ecommerceOrder = data.ecommerceOrder;
    this.ecommerceVendors = data.ecommerceVendors;
    this.dateInitiated = new Date(data.dateInitiated);
    if (data.dateCompleted) {
      this.dateCompleted = new Date(data.dateCompleted);
    }
    this.commerceUser = data.commerceUser;
    this.status = data.status;
    this.amount = data.amount;
    this.paymentMeta = data.paymentMeta;
  }

  /**
   * Retrieves all orderrefunds with pagination.
   *
   * @param {number} offset - The starting point for the retrieval.
   * @param {number} limit - The number of records to retrieve.
   * @returns {Promise<{ count: number; dataArr: EcommerceOrderRefund[] }>} A promise resolving
   * to an object containing count and data array.
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TecommerceOrderRefundProps>>(`${EcommerceOrderRefund.baseUrl}?${query}`);
    const orderrefunds = await lastValueFrom(observer$);

    return {
      count: orderrefunds.count,
      dataArr: orderrefunds.data.map((val) => new EcommerceOrderRefund(val))
    };
  }

  /**
   * Filters orderrefunds based on the provided criteria.
   *
   * @param {IfilterProps} filter - The filter criteria.
   * @returns {Promise<{ count: number; dataArr: EcommerceOrderRefund[] }>} A promise resolving
   * to an object containing count and data array.
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TecommerceOrderRefundProps>>(
        `${EcommerceOrderRefund.baseUrl}/filter`,
        filter
      );
    const orderrefunds = await lastValueFrom(observer$);

    return {
      count: orderrefunds.count,
      dataArr: orderrefunds.data.map((val) => new EcommerceOrderRefund(val))
    };
  }

  /**
   * Retrieves a single orderrefund by resource ID.
   *
   * @param {string} resourceId - The ID of the orderrefund to retrieve.
   * @returns {Promise<EcommerceOrderRefund>} The retrieved `EcommerceOrderRefund` instance.
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TecommerceOrderRefundProps>(`${EcommerceOrderRefund.baseUrl}/${resourceId}`);
    const orderrefund = await lastValueFrom(observer$);

    return new EcommerceOrderRefund(orderrefund);
  }

  /**
   * Creates a new order refund with the provided data.
   *
   * @param {TecommerceOrderRefundRequest<true>} payload - The order refund data to create
   * @returns {Promise<EcommerceOrderRefund>} The newly created EcommerceOrderRefund instance
   * @throws {Error} When the creation fails due to invalid data or API error
   *
   * @example
   * const refund = await EcommerceOrderRefund.add({
   *   orderId: 'order123',
   *   amount: 99.99,
   *   reason: 'Customer requested cancellation'
   * });
   */
  static async add(payload: TecommerceOrderRefundRequest<true>) {
    const observer$ = CommerceClient.ehttp
      .makePost<TecommerceOrderRefundProps>(
        `${EcommerceOrderRefund.baseUrl}`,
        payload
      );

    return new EcommerceOrderRefund(await lastValueFrom(observer$));
  }

  /**
   * Updates the current order refund with new data.
   *
   * @param {TecommerceOrderRefundRequest} payload - The updated refund data
   * @returns {Promise<EcommerceOrderRefund>} The updated EcommerceOrderRefund instance
   * @throws {Error} When the update fails or payload is invalid
   *
   * @example
   * const refund = await EcommerceOrderRefund.getOne('refund123');
   * const updated = await refund.update({
   *   status: 'processed',
   *   dateCompleted: new Date()
   * });
   */
  async update(payload: TecommerceOrderRefundRequest) {
    const observer$ = CommerceClient.ehttp
      .makePut<TecommerceOrderRefundProps>(
        `${EcommerceOrderRefund.baseUrl}/${this._id}`,
        payload
      );

    return new EcommerceOrderRefund(await lastValueFrom(observer$));
  }
}
